/**
 * `/app/documents` — the envelope list, and the composer's first step.
 *
 * This replaces a screen that asked the operator to PASTE AN ENVELOPE ID, which was an honest
 * placeholder for a real gap: until P4-15 there was no route that could create an envelope, so
 * every id had to come from somewhere else. It can now, and this is where.
 *
 * The list is cursor-paginated because the API is: the cursor is the sort key, opaque, and never an
 * offset — an offset would skip or repeat rows as envelopes are created underneath the page.
 *
 * It used to append pages behind a "Load more" and ask for no `limit`, so the first press showed 25
 * rows in one stretch and each press grew the page without bound. The founder's ask of 2026-09-16
 * was the same one the library had already answered — ten rows a page, with a size control — so
 * this now wears the same bar (`./Pagination`) over the same cursor stack: Previous is the cursor
 * that got us here rather than a re-derived guess, and there is no "page 3 of 12" because the API
 * returns no count and a total that is right until you filter is worse than no total.
 *
 * Nothing here talks to the database. It is the public API and nothing else (CLAUDE.md §4), which
 * is why the create form posts exactly the four fields `POST /v1/envelopes` accepts and no more.
 */
import {
  Badge,
  Button,
  Callout,
  Card,
  ErrorState,
  Icon,
  LoadingState,
  PageHeader,
} from '@flowza/ui'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { SUBJECT_MAX, generatedSubject } from '@flowza/core/envelopes/subject'
import { api, apiConfigured } from '../../api/client'
import { DEFAULT_PER_PAGE, Pagination, perPageFrom } from './Pagination'
import { createEnvelope } from '../../api/writes'
import type { EnvelopeResource } from '../../api/types'
import {
  ACCEPT_DOCUMENTS,
  ACCEPTED_DOCUMENTS_SENTENCE,
  isAcceptedDocumentName,
} from '@flowza/core/documents/files'
import { describe as messageOf } from './apiErrors'

export type { EnvelopeResource }

type Page = { readonly data: readonly EnvelopeResource[]; readonly next_cursor: string | null }

/** `public.create_envelope`'s own bound, from the module that owns it rather than restated here. */
const MAX_SUBJECT = SUBJECT_MAX

/**
 * How the envelope is being started: from a FILE, or from a subject somebody types.
 *
 * The founder's ask of 2026-09-15, from the Envelopes screen: "give me the option to Upload as
 * well, which is in International Workflow, where it gives the Subject automatically. Here we need
 * to type."
 *
 * `upload` picks the document first and names the envelope after it —
 * `Complete with FlowZa Sign: lease.pdf`, the filename WITH its extension, which is exactly what
 * `generatedSubject` produces for the international composer, so the two standards say the same
 * sentence. `create` is the flow this screen has always had, unchanged, for somebody who wants to
 * write the subject a signer will read.
 *
 * ## Why the generated subject is a MANUAL one here
 *
 * Under the international standard an envelope may be created with NO subject: the wrapper stores a
 * placeholder, marks `subject_source = 'auto'`, and the subject then follows the first document's
 * filename on the server. Under `flowza_sign` the same wrapper REFUSES a missing subject — that is
 * the standard's promise, and widening it would change what every existing client may send
 * (`packages/core/src/workflow`, `decideSubject`).
 *
 * So this generates the same string in the browser and sends it as the subject. The visible
 * consequence is honest and small: the box is filled in, editable, and stays what it says — it does
 * not silently re-follow a document swapped in later. The alternative was changing what the classic
 * standard accepts, which is a much larger promise than "put a filename in the box".
 */
type StartFrom = 'upload' | 'create'

const START_MODES: ReadonlyArray<{ readonly value: StartFrom; readonly label: string }> = [
  { value: 'upload', label: 'Upload a document' },
  { value: 'create', label: 'Write a subject' },
]

/**
 * The nine statuses, mapped to the three tones the design system has. `correcting` reads as
 * in-flight rather than as a warning: from the sender's side a correction is ordinary work.
 */
function toneFor(status: string): 'neutral' | 'success' | 'warning' {
  if (status === 'completed' || status === 'sealed') return 'success'
  if (status === 'declined' || status === 'expired' || status === 'voided') return 'warning'
  return 'neutral'
}

export default function Envelopes() {
  if (!apiConfigured()) {
    return (
      <ErrorState
        title="The API is not configured"
        body="VITE_API_URL is not set for this build, so envelopes cannot load."
      />
    )
  }
  return <EnvelopeList />
}

function EnvelopeList() {
  const navigate = useNavigate()
  const [search, setSearch] = useSearchParams()
  const perPage = perPageFrom(search)
  const [rows, setRows] = useState<readonly EnvelopeResource[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  /**
   * The cursors walked through to get here, and WHICH listing they belong to.
   *
   * The API pages by cursor, not by offset, so "page 4" is not a number you can ask for — it is
   * the cursor the third page handed back. Holding the stack makes Previous exact rather than
   * re-derived, and its length is the page number. `key` is what stops a cursor minted at one page
   * size being spent at another: those are different cuts of the same listing, and a stale cursor
   * would land somewhere arbitrary. Compared during render rather than reset in an effect, which
   * would show one frame of the wrong page every time the size changed.
   */
  const [paging, setPaging] = useState<{
    readonly key: string
    readonly stack: readonly string[]
  }>({ key: '', stack: [] })
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [subject, setSubject] = useState('')
  const [discloseProgress, setDiscloseProgress] = useState(false)
  const [startFrom, setStartFrom] = useState<StartFrom>('upload')
  const [picked, setPicked] = useState<File | null>(null)
  const fileInput = useRef<HTMLInputElement | null>(null)
  /**
   * The idempotency key for the create in flight, held across retries.
   *
   * Minted once per attempt-that-failed rather than once per call: pressing "Create" again after a
   * dropped connection must present the SAME key, or the server has no way to tell a retry from a
   * second envelope. Cleared on success, so the next envelope gets its own.
   */
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null)

  const listingKey = String(perPage)
  const stack = paging.key === listingKey ? paging.stack : []
  const cursor = stack[stack.length - 1]

  const load = useCallback(async () => {
    const query = new URLSearchParams({ limit: String(perPage) })
    if (cursor !== undefined) query.set('cursor', cursor)
    return api<Page>(`/v1/envelopes?${query}`)
  }, [perPage, cursor])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    load()
      .then((page) => {
        if (cancelled) return
        setRows(Array.isArray(page.data) ? page.data : [])
        setNextCursor(page.next_cursor ?? null)
        setLoadError(null)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        // Cleared, so a failed read cannot leave a Next button pointing at a cursor whose page
        // this screen never received.
        setNextCursor(null)
        setLoadError(messageOf(error))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [load])

  /** Forward one page, remembering the cursor that got us there so Previous is exact. */
  const nextPage = () => {
    if (nextCursor === null) return
    setPaging({ key: listingKey, stack: [...stack, nextCursor] })
  }
  const previousPage = () => {
    if (stack.length === 0) return
    setPaging({ key: listingKey, stack: stack.slice(0, -1) })
  }

  /**
   * A chosen file names the envelope, unless somebody has already written a subject of their own.
   *
   * The guard is the whole of the courtesy: typing a subject and THEN picking a document must not
   * silently overwrite the sentence a sender wrote for a signer to read. Re-picking replaces a
   * generated subject, because that one is this screen's own suggestion.
   */
  const onPickFile = useCallback(
    (file: File | undefined) => {
      if (file === undefined) return
      setCreateError(null)
      if (!isAcceptedDocumentName(file.name)) {
        setPicked(null)
        setCreateError(
          `A ${file.name.split('.').pop()?.toUpperCase() ?? 'file with no extension'} is not a document this can send. FlowZa Sign takes ${ACCEPTED_DOCUMENTS_SENTENCE}.`,
        )
        return
      }
      setPicked(file)
      setSubject((current) =>
        current.trim() === '' || current === generatedSubject(picked?.name ?? '')
          ? generatedSubject(file.name)
          : current,
      )
    },
    [picked],
  )

  async function create(event: React.FormEvent) {
    event.preventDefault()
    const trimmed = subject.trim()
    if (trimmed === '') return
    setCreating(true)
    setCreateError(null)
    try {
      // The client mints the Idempotency-Key, so a double-submit or a dropped connection replays
      // rather than placing a second envelope in front of the same counterparty. Minted HERE and
      // not inside `createEnvelope`, so a retry after a failure reuses it — a key generated per
      // call would be new every time, which is the shape idempotency exists to prevent.
      const key = idempotencyKey ?? crypto.randomUUID()
      setIdempotencyKey(key)
      const created = await createEnvelope({
        idempotencyKey: key,
        subject: trimmed,
        discloseProgress,
      })
      setSubject('')
      setIdempotencyKey(null)
      // Straight into SET UP ENVELOPE — the one page that carries documents, recipients and the
      // message, under both standards since ADR 0102. An envelope with no recipients is not yet
      // anything, and making the operator find it again in the list would be a step for the
      // software's benefit.
      //
      // The file rides in the history entry's state rather than being uploaded here, so the ONE
      // screen that knows how to upload a document — its progress, its conversion, a
      // password-protected PDF, a retry that reuses its idempotency key — is still the only screen
      // that does it. A `File` is structured-cloneable, so `history.pushState` carries it; the
      // composer takes it on mount and clears it so a reload does not upload it twice.
      const state = picked === null ? undefined : { state: { pickedFiles: [picked] } }
      setPicked(null)
      await navigate(`/app/envelopes/${created.id}/setup`, state)
    } catch (error: unknown) {
      setCreateError(messageOf(error))
    } finally {
      setCreating(false)
    }
  }

  return (
    <div>
      <PageHeader
        eyebrow="Workspace"
        title="Envelopes"
        description="Create an envelope, add its documents, place fields, then send it for signature."
      />

      <div className="mt-6">
        <Card title="New envelope">
          {/*
            Two ways in, named on the screen rather than discovered. Upload is first and is the
            default because it is the one that needs no typing: the document is what a sender has in
            front of them, and the subject follows from it.
          */}
          <div
            role="radiogroup"
            aria-label="How to start this envelope"
            className="border-border-subtle mb-4 inline-flex rounded-md border p-0.5"
          >
            {START_MODES.map((mode) => (
              <button
                key={mode.value}
                type="button"
                role="radio"
                aria-checked={startFrom === mode.value}
                onClick={() => {
                  setStartFrom(mode.value)
                  setCreateError(null)
                }}
                className={`h-control rounded-sm px-3 text-sm font-medium ${
                  startFrom === mode.value
                    ? 'bg-accent text-text-on-accent'
                    : 'text-text-muted hover:text-text-primary'
                }`}
              >
                {mode.label}
              </button>
            ))}
          </div>

          <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => void create(e)}>
            {startFrom === 'upload' ? (
              <div className="flex w-full flex-col gap-2">
                <input
                  ref={fileInput}
                  type="file"
                  accept={ACCEPT_DOCUMENTS}
                  className="hidden"
                  onChange={(e) => {
                    onPickFile(e.target.files?.[0])
                    // Cleared so choosing the SAME file twice fires onChange the second time.
                    e.target.value = ''
                  }}
                />
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={creating}
                    onClick={() => fileInput.current?.click()}
                  >
                    <Icon name="upload" size={16} className="mr-1" />
                    {picked === null ? 'Choose a document' : 'Choose a different document'}
                  </Button>
                  <span className="text-text-muted min-w-0 truncate text-sm">
                    {picked === null ? `${ACCEPTED_DOCUMENTS_SENTENCE}.` : picked.name}
                  </span>
                </div>
              </div>
            ) : null}

            <label className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-text-primary text-sm font-medium">Subject</span>
              <input
                value={subject}
                maxLength={MAX_SUBJECT}
                onChange={(e) => setSubject(e.currentTarget.value)}
                placeholder={
                  startFrom === 'upload'
                    ? 'Named after the document you choose'
                    : 'Consultancy agreement — Acme Ltd'
                }
                className="h-control border-border-strong bg-surface-raised text-text-primary w-full rounded-sm border px-3 text-sm"
              />
            </label>
            <Button
              type="submit"
              disabled={
                creating || subject.trim() === '' || (startFrom === 'upload' && picked === null)
              }
            >
              {creating ? 'Creating…' : 'Create'}
              <Icon name="chevron-right" size={16} className="ml-1" />
            </Button>
          </form>

          {/*
            Off by default, and asked at CREATION because that is the only time it can be set: the
            column is insertable and not updatable on purpose, since changing who can see whom on a
            live envelope is a correction rather than an edit.
          */}
          <label className="mt-3 flex items-start gap-2">
            <input
              type="checkbox"
              checked={discloseProgress}
              onChange={(e) => setDiscloseProgress(e.currentTarget.checked)}
              className="mt-1"
            />
            <span className="text-text-primary text-sm">
              Let the recipients see each other
              <span className="text-text-muted block">
                Everyone on the envelope sees the other names, the order, and who has signed. Right
                for an internal approval chain; usually wrong when the other party is outside your
                organization, because it tells them who else is at the table.
              </span>
            </span>
          </label>
          <p className="text-text-muted mb-0 mt-3 text-sm">
            The subject is what a signer sees first, in the email and at the top of the ceremony.
            {startFrom === 'upload'
              ? ' Choosing a document fills it in from the file name — the same sentence the International standard writes — and you can edit it before creating.'
              : ''}
          </p>
          {createError !== null && (
            <div className="mt-3">
              <Callout tone="warning" title="The envelope was not created">
                {createError}
              </Callout>
            </div>
          )}
        </Card>
      </div>

      <div className="mt-6">
        {loading ? (
          <LoadingState label="Loading envelopes" />
        ) : loadError !== null ? (
          <ErrorState title="Envelopes could not load" body={loadError} />
        ) : rows.length === 0 ? (
          /*
            Two different facts, and the old single sentence was wrong about one of them. An empty
            FIRST page means this organization has no envelopes; an empty later page means the list
            shrank under the reader — somebody archived the rows this cursor pointed at — and
            telling them to "create one above" while they stand on page 4 reads as the list having
            lost their work. The second case also needs a way back, which a bare Card has not got.
          */
          <Card title={stack.length === 0 ? 'No envelopes yet' : 'Nothing on this page'}>
            <p className="text-text-muted mb-0 text-sm">
              {stack.length === 0
                ? 'Create one above. Nothing is sent to anybody until you add documents, name the recipients and press send.'
                : 'These rows have moved or been archived since you turned the page.'}
            </p>
            {stack.length === 0 ? null : (
              <div className="mt-3">
                <Button variant="secondary" onClick={previousPage}>
                  Previous page
                </Button>
              </div>
            )}
          </Card>
        ) : (
          <Card
            title={
              stack.length === 0 && nextCursor === null
                ? `${rows.length} envelope${rows.length === 1 ? '' : 's'}`
                : 'Envelopes'
            }
          >
            <ul className="divide-border-subtle -my-2 divide-y">
              {rows.map((row) => (
                <li key={row.id} className="flex flex-wrap items-center gap-3 py-3">
                  <Link
                    to={`/app/envelopes/${row.id}/documents`}
                    className="text-text-primary min-w-0 flex-1 truncate text-sm font-medium hover:underline"
                  >
                    {row.subject}
                  </Link>
                  <Badge tone={toneFor(row.status)}>{row.status}</Badge>
                  <span className="text-text-muted text-sm">
                    {row.counts.documents} doc{row.counts.documents === 1 ? '' : 's'} ·{' '}
                    {row.counts.signers} signer{row.counts.signers === 1 ? '' : 's'}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-4">
              <Pagination
                perPage={perPage}
                noun="envelope"
                page={stack.length + 1}
                rowsOnPage={rows.length}
                hasNext={nextCursor !== null}
                hasPrevious={stack.length > 0}
                onPerPageChange={(n) => {
                  // Back to page 1: the cursors in hand cut the listing into different pages, so
                  // spending one after a resize would land somewhere arbitrary. Writing the param
                  // re-keys the stack, which is dropped on the same render.
                  const next = new URLSearchParams(search)
                  if (n === DEFAULT_PER_PAGE) next.delete('per')
                  else next.set('per', String(n))
                  setSearch(next, { replace: true })
                }}
                onNext={nextPage}
                onPrevious={previousPage}
              />
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}
